import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Card from '../components/ui/Card';
import DateSelect from '../components/ui/DateSelect';
import Button from '../components/ui/Button';
import Icons from '../components/Icons';
import SearchableSelect from '../components/ui/SearchableSelect';
import StaffService from '../services/StaffService';
import ActivityService from '../services/ActivityService';
import ExternalMessageService from '../services/ExternalMessageService';
import { useStudentStore } from '../store/StudentStore';
import { useUser } from '../store/UserContext';

const buildDoc = () => ({
  id: `doc-${Math.random().toString(36).slice(2, 8)}`,
  name: '',
  file: null,
  expiryDate: '',
  fileName: '',
  linkUrl: '',
  archived: false
});

const buildTemplate = () => ({
  id: `tpl-${Math.random().toString(36).slice(2, 8)}`,
  title: '',
  duration: '',
  focus: ''
});

const buildLessonPlan = () => ({
  id: `plan-${Math.random().toString(36).slice(2, 8)}`,
  title: '',
  date: '',
  file: null,
  notes: '',
  fileName: '',
  linkUrl: '',
  archived: false
});

export default function ActivitiesEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const activityService = new ActivityService();
  const { students } = useStudentStore();
  const { user } = useUser();
  const messageService = useMemo(() => new ExternalMessageService(), []);
  const staffService = useMemo(() => new StaffService(), []);
  const staffList = useMemo(() => staffService.list(), [staffService]);
  const staffOptions = useMemo(
    () =>
      staffList.map((staff) => ({
        value: staff.name,
        label: staff.name,
        meta: staff.role || staff.title || ''
      })),
    [staffList]
  );
  const [activities, setActivities] = useState(() => activityService.list());
  const [formError, setFormError] = useState('');
  const [form, setForm] = useState({
    name: '',
    date: new Date().toISOString().slice(0, 10),
    time: '',
    durationHours: '',
    capacity: '',
    location: '',
    lead: '',
    staffAvailability: '',
    status: 'scheduled',
    notes: '',
    courseFee: '',
    eventFee: '',
    otherFee: '',
    emergencyContactName: '',
    emergencyContactRole: '',
    emergencyContactPhone: '',
    emergencyContactEmail: '',
    trackerProvider: '',
    trackerId: '',
    trackerNotes: '',
    consentRequired: true,
    consentStatus: 'pending',
    riskAssessmentComplete: false,
    medicalNotes: '',
    safeguardingNotes: '',
    supervisionRatio: '',
    transportPlan: '',
    meetingPoint: '',
    collectionPlan: '',
    emergencyPlan: ''
  });
  const [templates, setTemplates] = useState([buildTemplate()]);
  const [documents, setDocuments] = useState([buildDoc()]);
  const [lessonPlans, setLessonPlans] = useState([buildLessonPlan()]);
  const [linkStatus, setLinkStatus] = useState('');

  const activeDocuments = documents.filter((doc) => !doc.archived);
  const archivedDocuments = documents.filter((doc) => doc.archived);
  const activeLessonPlans = lessonPlans.filter((plan) => !plan.archived);
  const archivedLessonPlans = lessonPlans.filter((plan) => plan.archived);

  const activity = activities.find((item) => item.id === id);

  useEffect(() => {
    if (!activity) return;
    setForm({
      name: activity.name || '',
      date: activity.date || new Date().toISOString().slice(0, 10),
      time: activity.time || '',
      durationHours: activity.durationHours ? String(activity.durationHours) : '',
      capacity: activity.capacity ? String(activity.capacity) : '',
      location: activity.location || '',
      lead: activity.lead || '',
      staffAvailability: activity.staffAvailability || '',
      status: activity.status || 'scheduled',
      notes: activity.notes || '',
      courseFee: activity.fees?.courseFee ? String(activity.fees.courseFee) : '',
      eventFee: activity.fees?.eventFee ? String(activity.fees.eventFee) : '',
      otherFee: activity.fees?.otherFee ? String(activity.fees.otherFee) : '',
      emergencyContactName: activity.emergencyContact?.name || '',
      emergencyContactRole: activity.emergencyContact?.role || '',
      emergencyContactPhone: activity.emergencyContact?.phone || '',
      emergencyContactEmail: activity.emergencyContact?.email || '',
      trackerProvider: activity.tracker?.provider || '',
      trackerId: activity.tracker?.id || '',
      trackerNotes: activity.tracker?.notes || '',
      consentRequired: activity.safeguarding?.consentRequired ?? true,
      consentStatus: activity.safeguarding?.consentStatus || 'pending',
      riskAssessmentComplete: activity.safeguarding?.riskAssessmentComplete ?? false,
      medicalNotes: activity.safeguarding?.medicalNotes || '',
      safeguardingNotes: activity.safeguarding?.safeguardingNotes || '',
      supervisionRatio: activity.safeguarding?.supervisionRatio || '',
      transportPlan: activity.safeguarding?.transportPlan || '',
      meetingPoint: activity.safeguarding?.meetingPoint || '',
      collectionPlan: activity.safeguarding?.collectionPlan || '',
      emergencyPlan: activity.safeguarding?.emergencyPlan || ''
    });

    setTemplates(
      activity.templates?.length
        ? activity.templates.map((tpl) => ({
            id: tpl.id || `tpl-${Math.random().toString(36).slice(2, 8)}`,
            title: tpl.title || '',
            duration: tpl.duration || '',
            focus: tpl.focus || ''
          }))
        : [buildTemplate()]
    );

    setDocuments(
      activity.documents?.length
        ? activity.documents.map((doc) => ({
            id: doc.id || `doc-${Math.random().toString(36).slice(2, 8)}`,
            name: doc.name || '',
            file: null,
            expiryDate: doc.expiryDate || '',
            fileName: doc.fileName || '',
            linkUrl: doc.linkUrl || '',
            archived: Boolean(doc.archived)
          }))
        : [buildDoc()]
    );

    setLessonPlans(
      activity.lessonPlans?.length
        ? activity.lessonPlans.map((plan) => ({
            id: plan.id || `plan-${Math.random().toString(36).slice(2, 8)}`,
            title: plan.title || '',
            date: plan.date || '',
            file: null,
            notes: plan.notes || '',
            fileName: plan.fileName || '',
            linkUrl: plan.linkUrl || '',
            archived: Boolean(plan.archived)
          }))
        : [buildLessonPlan()]
    );
  }, [activity]);

  const addTemplate = () => setTemplates((prev) => [...prev, buildTemplate()]);
  const removeTemplate = (templateId) =>
    setTemplates((prev) => prev.filter((item) => item.id !== templateId));
  const updateTemplate = (templateId, updates) =>
    setTemplates((prev) => prev.map((item) => (item.id === templateId ? { ...item, ...updates } : item)));

  const addDocument = () => setDocuments((prev) => [...prev, buildDoc()]);
  const removeDocument = (docId) => setDocuments((prev) => prev.filter((doc) => doc.id !== docId));
  const updateDocument = (docId, updates) =>
    setDocuments((prev) => prev.map((doc) => (doc.id === docId ? { ...doc, ...updates } : doc)));

  const addLessonPlan = () => setLessonPlans((prev) => [...prev, buildLessonPlan()]);
  const removeLessonPlan = (planId) => setLessonPlans((prev) => prev.filter((plan) => plan.id !== planId));
  const updateLessonPlan = (planId, updates) =>
    setLessonPlans((prev) => prev.map((plan) => (plan.id === planId ? { ...plan, ...updates } : plan)));

  const handlePasteLink = async (applyLink) => {
    if (!navigator?.clipboard?.readText) {
      setLinkStatus('Clipboard access is not available in this browser.');
      return;
    }
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        applyLink(text.trim());
        setLinkStatus('Link pasted from clipboard.');
      }
    } catch (error) {
      setLinkStatus('Unable to read clipboard. Please paste manually.');
    }
  };

  const shareLinkToStudents = ({ title, linkUrl, typeLabel }) => {
    if (!linkUrl) {
      setLinkStatus('Add a link before sharing with students.');
      return;
    }
    if (!user) {
      setLinkStatus('Sign in to share links.');
      return;
    }
    const recipients = students
      .filter((student) =>
        (student.activities || []).some((entry) => entry.activityId === activity?.id || entry.id === activity?.id)
      )
      .map((student) => ({ id: student.id, name: student.name, type: 'student' }));
    if (recipients.length === 0) {
      setLinkStatus('No enrolled students to notify.');
      return;
    }
    messageService.sendFromStaff({
      subject: `${typeLabel} shared: ${title}`,
      body: `A new ${typeLabel.toLowerCase()} has been shared for ${activity?.name || 'this activity'}.\n\nAccess: ${linkUrl}`,
      fromUser: user,
      recipients,
      category: 'class-notes',
      references: [{ type: 'activity', id: activity?.id || id, label: activity?.name || 'Activity' }]
    });
    setLinkStatus('Link shared with enrolled students.');
  };

  const handleSave = (event) => {
    event.preventDefault();
    if (!activity) return;
    if (!form.name.trim()) {
      setFormError('Activity name is required.');
      return;
    }
    if (!form.date) {
      setFormError('Activity date is required.');
      return;
    }
    if (!form.capacity) {
      setFormError('Capacity is required.');
      return;
    }

    const validTemplates = templates.filter((tpl) => tpl.title.trim());
    const validDocuments = documents
      .filter((doc) => doc.name.trim() || doc.file || doc.linkUrl)
      .map((doc) => ({
        id: doc.id,
        name: doc.name.trim() || (doc.file ? doc.file.name : 'Document'),
        expiryDate: doc.expiryDate || null,
        fileName: doc.file ? doc.file.name : doc.fileName || null,
        linkUrl: doc.linkUrl || '',
        archived: Boolean(doc.archived),
        uploadedAt: new Date().toISOString()
      }));
    const validLessonPlans = lessonPlans
      .filter((plan) => plan.title.trim() || plan.file || plan.linkUrl)
      .map((plan) => ({
        id: plan.id,
        title: plan.title.trim() || (plan.file ? plan.file.name : 'Lesson plan'),
        date: plan.date || null,
        fileName: plan.file ? plan.file.name : plan.fileName || null,
        notes: plan.notes || '',
        linkUrl: plan.linkUrl || '',
        archived: Boolean(plan.archived),
        uploadedAt: new Date().toISOString()
      }));

    const updatedActivity = {
      ...activity,
      name: form.name.trim(),
      date: form.date,
      time: form.time || 'TBD',
      durationHours: Number(form.durationHours || 0),
      capacity: Number(form.capacity),
      location: form.location || 'TBD',
      lead: form.lead || 'Unassigned',
      staffAvailability: form.staffAvailability || '',
      status: form.status,
      notes: form.notes || '',
      emergencyContact: {
        name: form.emergencyContactName || '',
        role: form.emergencyContactRole || '',
        phone: form.emergencyContactPhone || '',
        email: form.emergencyContactEmail || ''
      },
      tracker: {
        provider: form.trackerProvider || '',
        id: form.trackerId || '',
        notes: form.trackerNotes || ''
      },
      safeguarding: {
        consentRequired: Boolean(form.consentRequired),
        consentStatus: form.consentStatus || 'pending',
        riskAssessmentComplete: Boolean(form.riskAssessmentComplete),
        medicalNotes: form.medicalNotes || '',
        safeguardingNotes: form.safeguardingNotes || '',
        supervisionRatio: form.supervisionRatio || '',
        transportPlan: form.transportPlan || '',
        meetingPoint: form.meetingPoint || '',
        collectionPlan: form.collectionPlan || '',
        emergencyPlan: form.emergencyPlan || ''
      },
      fees: {
        courseFee: Number(form.courseFee || 0),
        eventFee: Number(form.eventFee || 0),
        otherFee: Number(form.otherFee || 0)
      },
      templates: validTemplates.map((tpl) => ({
        id: tpl.id,
        title: tpl.title.trim(),
        duration: tpl.duration.trim(),
        focus: tpl.focus.trim()
      })),
      documents: validDocuments,
      lessonPlans: validLessonPlans
    };

    const next = activityService.update(updatedActivity);
    setActivities(next);
    navigate('/programmes');
  };

  if (!activity) {
    return (
      <Card className="p-6">
        <p className="sp-muted">Activity not found.</p>
        <Button variant="secondary" className="mt-4" onClick={() => navigate('/programmes')}>
          Back to activities
        </Button>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="sp-value" style={{ fontSize: '20px', fontWeight: 600 }}>Edit Activity</h2>
          <p className="sp-muted">Update timing, capacity, fees, and documentation.</p>
        </div>
        <Button variant="secondary" onClick={() => navigate('/programmes')}>
          Back to activities
        </Button>
      </div>

      <Card className="p-6">
        <form onSubmit={handleSave} className="space-y-4">
          {formError && <div className="sp-panel sp-panel--muted p-3 text-sm">{formError}</div>}

          <div>
            <label className="sp-label">Activity name</label>
            <input
              className="sp-input mt-2 w-full"
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
              placeholder="e.g. Museum Trip"
              required
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="sp-label">Date</label>
              <DateSelect
                value={form.date}
                onChange={(value) => setForm({ ...form, date: value })}
                className="mt-2 w-full"
              />
            </div>
            <div>
              <label className="sp-label">Time</label>
              <input
                className="sp-input mt-2 w-full"
                value={form.time}
                onChange={(event) => setForm({ ...form, time: event.target.value })}
                placeholder="e.g. 10:00-15:00"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="sp-label">Duration (hours)</label>
              <input
                type="number"
                min="0"
                step="0.25"
                className="sp-input mt-2 w-full"
                value={form.durationHours}
                onChange={(event) => setForm({ ...form, durationHours: event.target.value })}
                placeholder="e.g. 3"
              />
            </div>
            <div>
              <label className="sp-label">Capacity</label>
              <input
                type="number"
                min="0"
                className="sp-input mt-2 w-full"
                value={form.capacity}
                onChange={(event) => setForm({ ...form, capacity: event.target.value })}
                placeholder="e.g. 20"
                required
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="sp-label">Location</label>
              <input
                className="sp-input mt-2 w-full"
                value={form.location}
                onChange={(event) => setForm({ ...form, location: event.target.value })}
                placeholder="e.g. Manchester Museum"
              />
            </div>
            <div>
              <label className="sp-label">Lead support worker</label>
              <SearchableSelect
                value={form.lead}
                options={[{ value: '', label: 'Unassigned' }, ...staffOptions]}
                placeholder="Search staff"
                emptyLabel="No staff found."
                ariaLabel="Search lead support worker"
                className="mt-2"
                onChange={(value) => setForm({ ...form, lead: value })}
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="sp-label">Staff availability</label>
              <input
                className="sp-input mt-2 w-full"
                value={form.staffAvailability}
                onChange={(event) => setForm({ ...form, staffAvailability: event.target.value })}
                placeholder="e.g. 2 staff required"
              />
            </div>
            <div>
              <label className="sp-label">Status</label>
              <select
                value={form.status}
                onChange={(event) => setForm({ ...form, status: event.target.value })}
                className="sp-select mt-2 w-full"
              >
                <option value="scheduled">Scheduled</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="sp-label">Course fee</label>
              <input
                type="number"
                min="0"
                className="sp-input mt-2 w-full"
                value={form.courseFee}
                onChange={(event) => setForm({ ...form, courseFee: event.target.value })}
              />
            </div>
            <div>
              <label className="sp-label">Event fee</label>
              <input
                type="number"
                min="0"
                className="sp-input mt-2 w-full"
                value={form.eventFee}
                onChange={(event) => setForm({ ...form, eventFee: event.target.value })}
              />
            </div>
            <div>
              <label className="sp-label">Other fees</label>
              <input
                type="number"
                min="0"
                className="sp-input mt-2 w-full"
                value={form.otherFee}
                onChange={(event) => setForm({ ...form, otherFee: event.target.value })}
              />
            </div>
          </div>

          <div>
            <label className="sp-label">Notes</label>
            <input
              className="sp-input mt-2 w-full"
              value={form.notes}
              onChange={(event) => setForm({ ...form, notes: event.target.value })}
              placeholder="Optional notes"
            />
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="sp-value">Emergency Contacts</h4>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="sp-label">Primary contact name</label>
                <input
                  className="sp-input mt-2 w-full"
                  value={form.emergencyContactName}
                  onChange={(event) => setForm({ ...form, emergencyContactName: event.target.value })}
                  placeholder="e.g. Duty Manager"
                />
              </div>
              <div>
                <label className="sp-label">Role</label>
                <input
                  className="sp-input mt-2 w-full"
                  value={form.emergencyContactRole}
                  onChange={(event) => setForm({ ...form, emergencyContactRole: event.target.value })}
                  placeholder="e.g. Safeguarding Lead"
                />
              </div>
              <div>
                <label className="sp-label">Phone</label>
                <input
                  className="sp-input mt-2 w-full"
                  value={form.emergencyContactPhone}
                  onChange={(event) => setForm({ ...form, emergencyContactPhone: event.target.value })}
                  placeholder="e.g. 07700 123456"
                />
              </div>
              <div>
                <label className="sp-label">Email</label>
                <input
                  className="sp-input mt-2 w-full"
                  value={form.emergencyContactEmail}
                  onChange={(event) => setForm({ ...form, emergencyContactEmail: event.target.value })}
                  placeholder="e.g. safeguarding@centre.org"
                />
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="sp-value">Tracker / AirTag</h4>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="sp-label">Provider</label>
                <input
                  className="sp-input mt-2 w-full"
                  value={form.trackerProvider}
                  onChange={(event) => setForm({ ...form, trackerProvider: event.target.value })}
                  placeholder="e.g. Apple AirTag"
                />
              </div>
              <div>
                <label className="sp-label">Tracker ID</label>
                <input
                  className="sp-input mt-2 w-full"
                  value={form.trackerId}
                  onChange={(event) => setForm({ ...form, trackerId: event.target.value })}
                  placeholder="e.g. AT-22-104"
                />
              </div>
              <div>
                <label className="sp-label">Notes</label>
                <input
                  className="sp-input mt-2 w-full"
                  value={form.trackerNotes}
                  onChange={(event) => setForm({ ...form, trackerNotes: event.target.value })}
                  placeholder="Assigned device or bag"
                />
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="sp-value">Safeguarding Details</h4>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="sp-label">Consent required</label>
                <label className="flex items-center gap-2 text-sm mt-2">
                  <input
                    type="checkbox"
                    checked={form.consentRequired}
                    onChange={(event) => setForm({ ...form, consentRequired: event.target.checked })}
                  />
                  Consent is required for attendance
                </label>
              </div>
              <div>
                <label className="sp-label">Consent status</label>
                <select
                  value={form.consentStatus}
                  onChange={(event) => setForm({ ...form, consentStatus: event.target.value })}
                  className="sp-select mt-2 w-full"
                >
                  <option value="pending">Pending</option>
                  <option value="received">Received</option>
                  <option value="not-required">Not required</option>
                </select>
              </div>
              <div>
                <label className="sp-label">Risk assessment complete</label>
                <label className="flex items-center gap-2 text-sm mt-2">
                  <input
                    type="checkbox"
                    checked={form.riskAssessmentComplete}
                    onChange={(event) => setForm({ ...form, riskAssessmentComplete: event.target.checked })}
                  />
                  Completed and filed
                </label>
              </div>
              <div>
                <label className="sp-label">Medical notes</label>
                <input
                  className="sp-input mt-2 w-full"
                  value={form.medicalNotes}
                  onChange={(event) => setForm({ ...form, medicalNotes: event.target.value })}
                  placeholder="Allergies, medication, access needs"
                />
              </div>
              <div>
                <label className="sp-label">Supervision ratio</label>
                <input
                  className="sp-input mt-2 w-full"
                  value={form.supervisionRatio}
                  onChange={(event) => setForm({ ...form, supervisionRatio: event.target.value })}
                  placeholder="e.g. 1:3 staff to student"
                />
              </div>
              <div>
                <label className="sp-label">Transport plan</label>
                <input
                  className="sp-input mt-2 w-full"
                  value={form.transportPlan}
                  onChange={(event) => setForm({ ...form, transportPlan: event.target.value })}
                  placeholder="e.g. Minibus pickup at 09:30"
                />
              </div>
              <div>
                <label className="sp-label">Meeting point</label>
                <input
                  className="sp-input mt-2 w-full"
                  value={form.meetingPoint}
                  onChange={(event) => setForm({ ...form, meetingPoint: event.target.value })}
                  placeholder="e.g. Reception at 09:15"
                />
              </div>
              <div>
                <label className="sp-label">Collection plan</label>
                <input
                  className="sp-input mt-2 w-full"
                  value={form.collectionPlan}
                  onChange={(event) => setForm({ ...form, collectionPlan: event.target.value })}
                  placeholder="e.g. Collect from main gate"
                />
              </div>
              <div className="md:col-span-2">
                <label className="sp-label">Safeguarding notes</label>
                <textarea
                  className="sp-textarea mt-2 w-full"
                  rows={3}
                  value={form.safeguardingNotes}
                  onChange={(event) => setForm({ ...form, safeguardingNotes: event.target.value })}
                  placeholder="Risk considerations, supervision notes, meeting points"
                />
              </div>
              <div className="md:col-span-2">
                <label className="sp-label">Emergency plan</label>
                <textarea
                  className="sp-textarea mt-2 w-full"
                  rows={2}
                  value={form.emergencyPlan}
                  onChange={(event) => setForm({ ...form, emergencyPlan: event.target.value })}
                  placeholder="Escalation steps, emergency contacts, regroup plan"
                />
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="sp-value">Templates</h4>
              <Button type="button" variant="secondary" size="sm" onClick={addTemplate}>
                Add Template
              </Button>
            </div>
            {templates.map((template) => (
              <div key={template.id} className="grid md:grid-cols-3 gap-3">
                <input
                  className="sp-input"
                  placeholder="Template title"
                  value={template.title}
                  onChange={(event) => updateTemplate(template.id, { title: event.target.value })}
                />
                <input
                  className="sp-input"
                  placeholder="Duration"
                  value={template.duration}
                  onChange={(event) => updateTemplate(template.id, { duration: event.target.value })}
                />
                <div className="flex items-center gap-2">
                  <input
                    className="sp-input flex-1"
                    placeholder="Focus"
                    value={template.focus}
                    onChange={(event) => updateTemplate(template.id, { focus: event.target.value })}
                  />
                  {templates.length > 1 && (
                    <Button type="button" size="sm" variant="ghost" onClick={() => removeTemplate(template.id)}>
                      Remove
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="sp-value">Documents</h4>
              <Button type="button" variant="secondary" size="sm" onClick={addDocument}>
                Add Document
              </Button>
            </div>
            {activeDocuments.map((doc) => (
              <div key={doc.id} className="grid md:grid-cols-3 gap-3">
                <div>
                  <label className="sp-label">Document name</label>
                  <input
                    className="sp-input mt-2 w-full"
                    placeholder="Document name"
                    value={doc.name}
                    onChange={(event) => updateDocument(doc.id, { name: event.target.value })}
                  />
                </div>
                <div>
                  <label className="sp-label">Upload</label>
                  <input
                    className="sp-input mt-2 w-full"
                    type="file"
                    onChange={(event) => updateDocument(doc.id, { file: event.target.files?.[0] || null })}
                  />
                  {(doc.file || doc.fileName) && (
                    <p className="text-xs sp-muted mt-1">{doc.file?.name || doc.fileName}</p>
                  )}
                </div>
                <div>
                  <label className="sp-label">Expiry date (optional)</label>
                  <div className="flex items-center gap-2 mt-2">
                    <DateSelect
                      value={doc.expiryDate}
                      onChange={(value) => updateDocument(doc.id, { expiryDate: value })}
                      className="w-full"
                    />
                    {documents.length > 1 && (
                      <Button type="button" size="sm" variant="ghost" onClick={() => removeDocument(doc.id)}>
                        Remove
                      </Button>
                    )}
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="sp-label">Share link</label>
                  <div className="flex items-center gap-2 mt-2">
                    <input
                      className="sp-input w-full"
                      value={doc.linkUrl || ''}
                      onChange={(event) => updateDocument(doc.id, { linkUrl: event.target.value })}
                      placeholder="https://drive.google.com/..."
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      aria-label="Paste link from clipboard"
                      onClick={() => handlePasteLink((text) => updateDocument(doc.id, { linkUrl: text }))}
                    >
                      <Icons.Clipboard />
                    </Button>
                  </div>
                  <p className="text-xs sp-muted mt-1">Use Google Drive or similar and paste a share link.</p>
                </div>
                <div className="flex items-center gap-2">
                  <label className="sp-label">Archive</label>
                  <input
                    type="checkbox"
                    checked={Boolean(doc.archived)}
                    onChange={(event) => updateDocument(doc.id, { archived: event.target.checked })}
                    aria-label="Archive document"
                  />
                </div>
                <div className="md:col-span-3 flex items-center justify-between flex-wrap gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() =>
                      shareLinkToStudents({
                        title: doc.name || doc.fileName || 'Document',
                        linkUrl: doc.linkUrl,
                        typeLabel: 'Document'
                      })
                    }
                  >
                    Share link to students
                  </Button>
                  {documents.length > 1 && (
                    <Button type="button" size="sm" variant="ghost" onClick={() => removeDocument(doc.id)}>
                      Remove
                    </Button>
                  )}
                </div>
              </div>
            ))}
            {archivedDocuments.length > 0 && (
              <div className="sp-panel sp-panel--muted p-3 text-sm">
                <div className="sp-value" style={{ fontWeight: 600 }}>Archived documents</div>
                <div className="mt-2 space-y-2">
                  {archivedDocuments.map((doc) => (
                    <div key={doc.id} className="flex items-center justify-between gap-2">
                      <div>
                        <div className="sp-value" style={{ fontWeight: 600 }}>{doc.name || doc.fileName || 'Document'}</div>
                        <div className="text-xs sp-muted">{doc.linkUrl || 'No link added'}</div>
                      </div>
                      <Button size="sm" variant="secondary" type="button" onClick={() => updateDocument(doc.id, { archived: false })}>
                        Restore
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="sp-value">Lesson Plans</h4>
              <Button type="button" variant="secondary" size="sm" onClick={addLessonPlan}>
                Add Lesson Plan
              </Button>
            </div>
            {activeLessonPlans.map((plan) => (
              <div key={plan.id} className="grid md:grid-cols-3 gap-3">
                <div>
                  <label className="sp-label">Title</label>
                  <input
                    className="sp-input mt-2 w-full"
                    placeholder="e.g. Session plan"
                    value={plan.title}
                    onChange={(event) => updateLessonPlan(plan.id, { title: event.target.value })}
                  />
                </div>
                <div>
                  <label className="sp-label">Session date</label>
                  <DateSelect
                    value={plan.date}
                    onChange={(value) => updateLessonPlan(plan.id, { date: value })}
                    className="mt-2 w-full"
                  />
                </div>
                <div>
                  <label className="sp-label">Upload</label>
                  <input
                    className="sp-input mt-2 w-full"
                    type="file"
                    onChange={(event) => updateLessonPlan(plan.id, { file: event.target.files?.[0] || null })}
                  />
                  {(plan.file || plan.fileName) && <p className="text-xs sp-muted mt-1">{plan.file?.name || plan.fileName}</p>}
                </div>
                <div className="md:col-span-2">
                  <label className="sp-label">Share link</label>
                  <div className="flex items-center gap-2 mt-2">
                    <input
                      className="sp-input w-full"
                      value={plan.linkUrl || ''}
                      onChange={(event) => updateLessonPlan(plan.id, { linkUrl: event.target.value })}
                      placeholder="https://drive.google.com/..."
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      aria-label="Paste link from clipboard"
                      onClick={() => handlePasteLink((text) => updateLessonPlan(plan.id, { linkUrl: text }))}
                    >
                      <Icons.Clipboard />
                    </Button>
                  </div>
                  <p className="text-xs sp-muted mt-1">Use Google Drive or similar and paste a share link.</p>
                </div>
                <div className="flex items-center gap-2">
                  <label className="sp-label">Archive</label>
                  <input
                    type="checkbox"
                    checked={Boolean(plan.archived)}
                    onChange={(event) => updateLessonPlan(plan.id, { archived: event.target.checked })}
                    aria-label="Archive lesson plan"
                  />
                </div>
                <div className="md:col-span-3">
                  <label className="sp-label">Notes</label>
                  <textarea
                    className="sp-textarea mt-2 w-full"
                    rows={2}
                    value={plan.notes}
                    onChange={(event) => updateLessonPlan(plan.id, { notes: event.target.value })}
                    placeholder="Key outcomes, resources, or homework."
                  />
                </div>
                <div className="md:col-span-3 flex items-center justify-between flex-wrap gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() =>
                      shareLinkToStudents({
                        title: plan.title || plan.fileName || 'Lesson plan',
                        linkUrl: plan.linkUrl,
                        typeLabel: 'Lesson plan'
                      })
                    }
                  >
                    Share link to students
                  </Button>
                  {lessonPlans.length > 1 && (
                    <Button type="button" size="sm" variant="ghost" onClick={() => removeLessonPlan(plan.id)}>
                      Remove
                    </Button>
                  )}
                </div>
              </div>
            ))}
            {archivedLessonPlans.length > 0 && (
              <div className="sp-panel sp-panel--muted p-3 text-sm">
                <div className="sp-value" style={{ fontWeight: 600 }}>Archived lesson plans</div>
                <div className="mt-2 space-y-2">
                  {archivedLessonPlans.map((plan) => (
                    <div key={plan.id} className="flex items-center justify-between gap-2">
                      <div>
                        <div className="sp-value" style={{ fontWeight: 600 }}>{plan.title || plan.fileName || 'Lesson plan'}</div>
                        <div className="text-xs sp-muted">{plan.linkUrl || 'No link added'}</div>
                      </div>
                      <Button size="sm" variant="secondary" type="button" onClick={() => updateLessonPlan(plan.id, { archived: false })}>
                        Restore
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {linkStatus && <div className="text-xs sp-muted">{linkStatus}</div>}

          <div className="flex items-center justify-end gap-2">
            <Button variant="secondary" type="button" onClick={() => navigate('/programmes')}>
              Cancel
            </Button>
            <Button type="submit">Save Activity</Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
